import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore, THEMES } from '../stores/themeStore';
import { useAuthStore } from '../stores/authStore';
import { colors } from '../styles/colors';
import { useState } from 'react';

export default function LandingPage() {
  const { i18n } = useTranslation();
  const chatTheme = useThemeStore((s) => s.chatTheme);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const initialized = useAuthStore((s) => s.initialized);
  const isKo = i18n.language === 'ko';
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Redirect to app if already logged in
  if (initialized && firebaseUser) {
    return <Navigate to="/app" replace />;
  }
  // Show nothing while auth is resolving to avoid flash
  if (!initialized) {
    return <div style={{ backgroundColor: '#0a0a0f', minHeight: '100vh' }} />;
  }

  const shareLinks = [
    { key: 'web' as const, label: 'Web', icon: '🌐', color: colors.textSecondary },
    { key: 'android' as const, label: 'Android', icon: '🤖', color: '#3ddc84' },
    { key: 'ios' as const, label: 'iOS', icon: '🍎', color: '#007aff' },
  ].filter((l) => !(l.key === 'android' && typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.getPlatform?.() === 'ios'));

  const handleShare = async (platform: 'web' | 'android' | 'ios') => {
    setShowShareMenu(false);
    const { shareAppForPlatform } = await import('../utils/share');
    await shareAppForPlatform(platform, i18n.language);
  };

  const features = isKo ? [
    { icon: '📡', title: '실시간 무전기 채팅', desc: 'GPS 위치가 주파수로 변환되어 같은 주파수의 사용자들과 실시간 소통' },
    { icon: '🗺️', title: '레이더 & 구글 지도', desc: '주변 사용자를 레이더와 구글 지도에서 실시간 확인' },
    { icon: '📍', title: '위치 메시지 드롭', desc: '특정 위치에 시간 제한 메시지를 남겨 주변 사용자에게 전달' },
    { icon: '✉️', title: '쪽지 (DM)', desc: '1:1 쪽지로 개인적인 대화를 주고받으세요' },
    { icon: '📋', title: '게시판', desc: '공지사항, 자유게시판에서 커뮤니티 활동' },
    { icon: '🔋', title: '배터리 시스템', desc: '메시지 송수신에 배터리(셀)를 사용하는 독특한 경험' },
  ] : [
    { icon: '📡', title: 'Live Walkie-Talkie Chat', desc: 'Your GPS location becomes a frequency — chat with users on the same wavelength' },
    { icon: '🗺️', title: 'Radar & Google Maps', desc: 'See nearby users on radar and Google Maps in real time' },
    { icon: '📍', title: 'Location Message Drops', desc: 'Drop time-limited messages at specific locations for nearby users' },
    { icon: '✉️', title: 'Direct Messages', desc: 'Send private 1:1 messages to other users' },
    { icon: '📋', title: 'Bulletin Board', desc: 'Community board for announcements and discussions' },
    { icon: '🔋', title: 'Battery System', desc: 'A unique cell-based economy for sending and receiving messages' },
  ];

  return (
    <div className="flex flex-col" style={{ backgroundColor: colors.background, height: '100vh' }}>

      {/* Header - sticky */}
      <header className="sticky top-0 z-10 flex shrink-0 items-center justify-between px-3 py-2.5"
        style={{ backgroundColor: colors.background, borderBottom: `1px solid ${colors.border}` }}>
        <h1 className="shrink-0 text-lg font-bold" style={{
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {isKo ? '우리들의 주파수' : 'OurHertz'}
        </h1>
        <div className="flex items-center gap-1">
          {/* Language */}
          <button onClick={() => { const l = i18n.language === 'ko' ? 'en' : 'ko'; i18n.changeLanguage(l); localStorage.setItem('ourhertz_language', JSON.stringify(l)); }}
            className="rounded px-1.5 py-1 text-[11px] font-bold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
            {isKo ? 'EN' : '한'}
          </button>
          {/* Theme */}
          <button onClick={() => { const ts = useThemeStore.getState(); const idx = THEMES.findIndex(t => t.key === ts.chatTheme); ts.setChatTheme(THEMES[(idx + 1) % THEMES.length].key); }}
            className="rounded px-1.5 py-1 text-[11px] font-bold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
            🎨
          </button>
          {/* Share */}
          <div className="relative">
            <button onClick={() => setShowShareMenu(!showShareMenu)}
              className="rounded px-1.5 py-1"
              style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            </button>
            {showShareMenu && (
              <div className="absolute right-0 top-full z-10 mt-1 overflow-hidden rounded-lg shadow-xl"
                style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}`, minWidth: 140 }}>
                {shareLinks.map(({ key, label, icon, color }) => (
                  <button key={key} onClick={() => handleShare(key)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-white/5"
                    style={{ color }}>{icon} {label}</button>
                ))}
              </div>
            )}
          </div>
          {/* Auth */}
          <Link to="/login" className="rounded px-2.5 py-1 text-[11px] font-bold"
            style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>
            {isKo ? '로그인' : 'Login'}
          </Link>
          <Link to="/signup" className="rounded px-2.5 py-1 text-[11px] font-bold text-white"
            style={{ backgroundColor: colors.primary }}>
            {isKo ? '회원가입' : 'Sign Up'}
          </Link>
        </div>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">

      {/* Hero */}
      <section className="flex flex-col items-center px-4 py-10 text-center">
        <h2 className="mb-2 text-2xl font-bold sm:text-3xl" style={{
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {isKo ? '내 위치가 주파수가 되다' : 'Your Location, Your Frequency'}
        </h2>
        <p className="mb-4 max-w-md text-sm leading-relaxed" style={{ color: colors.textSecondary, wordBreak: 'keep-all' }}>
          {isKo
            ? 'GPS 기반 무전기 채팅 서비스. 내 위치가 주파수가 되어 주변 사람들과 실시간으로 소통하세요.'
            : 'GPS-based walkie-talkie chat. Your location becomes your frequency — connect with nearby people in real time.'}
        </p>
        <div className="flex items-center gap-3 text-[11px]" style={{ color: colors.textMuted }}>
          <span>📡 {isKo ? '실시간 채팅' : 'Live Chat'}</span>
          <span>📍 {isKo ? '위치 메시지 드롭' : 'Location Drops'}</span>
          <span>🗺️ {isKo ? '레이더 지도' : 'Radar Map'}</span>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto w-full max-w-md px-4 pb-10">
        <div className="flex flex-col gap-2">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl p-3"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <span className="shrink-0 text-xl">{f.icon}</span>
              <div>
                <h3 className="mb-0.5 text-sm font-bold" style={{ color: colors.text }}>{f.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: colors.textMuted, wordBreak: 'keep-all' }}>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto px-4 py-4 text-center text-[10px]" style={{ color: colors.textMuted }}>
        <div className="mb-1 flex items-center justify-center gap-2">
          <a href={isKo ? '/terms_ko.html' : '/terms_en.html'} target="_blank" rel="noopener"
            style={{ color: colors.textSecondary, textDecoration: 'underline' }}>
            {isKo ? '서비스 이용약관' : 'Terms of Service'}
          </a>
          <span>|</span>
          <a href={isKo ? '/privacy_ko.html' : '/privacy_en.html'} target="_blank" rel="noopener"
            style={{ color: colors.textSecondary, textDecoration: 'underline' }}>
            {isKo ? '개인정보처리방침' : 'Privacy Policy'}
          </a>
        </div>
        <p>&copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{isKo ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.</p>
        <p className="mt-0.5">Web v{__APP_VERSION__}</p>
      </footer>

      </div>{/* end scrollable */}

      {showShareMenu && <div className="fixed inset-0 z-0" onClick={() => setShowShareMenu(false)} />}
    </div>
  );
}
